// Contracts — vista AUTH (2026-08-02). Staff-only magic link; there is no
// self-signup, an account must exist in `users` before a link is issued.
import { z } from 'zod';
import { VistaEmailSchema, VistaRoleSchema } from './enums';

// POST /api/vista/auth/request {email} → 200 always.
// The response is identical for known and unknown addresses: this endpoint is
// unauthenticated, and a differing answer would turn it into a staff directory.
export const VistaAuthRequestReqSchema = z.object({
  email: VistaEmailSchema,
  /** Токен проверки «человек ли это» — см. VistaLoginReqSchema. */
  turnstileToken: z.string().max(4096).optional(),
});
export type VistaAuthRequestReq = z.infer<typeof VistaAuthRequestReqSchema>;

export const VistaAuthRequestResSchema = z.object({ ok: z.literal(true) });
export type VistaAuthRequestRes = z.infer<typeof VistaAuthRequestResSchema>;

// POST /api/vista/auth/login {email, password} → session JWT. The primary
// door while email delivery is unavailable (0003): passwords are set by the
// manager, no self-service reset. Response indistinguishable for wrong email
// vs wrong password.
export const VistaPasswordSchema = z.string().min(8).max(200);

export const VistaLoginReqSchema = z.object({
  email: VistaEmailSchema,
  password: VistaPasswordSchema,
  /** Токен проверки «человек ли это». Необязателен: пока секрет на воркере не
   *  заведён, проверка пропускается — иначе выкат кода запер бы всех снаружи
   *  до появления секрета. */
  turnstileToken: z.string().max(4096).optional(),
});
export type VistaLoginReq = z.infer<typeof VistaLoginReqSchema>;

export const VistaLoginResSchema = z.object({ token: z.string().min(1) });
export type VistaLoginRes = z.infer<typeof VistaLoginResSchema>;

// POST /api/vista/auth/verify {token} → session JWT. Single-use: the worker
// burns the row with an UPDATE … WHERE used_at IS NULL guard.
export const VistaAuthVerifyReqSchema = z.object({ token: z.string().trim().min(20).max(200) });
export type VistaAuthVerifyReq = z.infer<typeof VistaAuthVerifyReqSchema>;

export const VistaAuthVerifyResSchema = z.object({ token: z.string().min(1) });
export type VistaAuthVerifyRes = z.infer<typeof VistaAuthVerifyResSchema>;

// A staff session's cabinet set may ALSO include 'contributor' when the same
// person's email owns a contributors row (F3, 2026-08-11: an admin/manager
// who shoots their own material). It never enters VISTA_ROLE_RANK/allows() —
// 'contributor' is a cabinet you switch INTO, not a staff privilege level,
// and the session JWT keeps carrying the highest STAFF role regardless (the
// switcher mints a second, independent contributor token instead — see
// POST /auth/as-contributor in profile.ts).
export const VistaMeRoleSchema = z.union([VistaRoleSchema, z.literal('contributor')]);
export type VistaMeRole = z.infer<typeof VistaMeRoleSchema>;

// GET /api/vista/auth/me → the caller. Two shapes, discriminated by role:
// staff (viewer/admin/manager) and contributor (their own cabinet, 0004).
// `showBalance` is per-person (kids 0020 heritage); `rateCents` on staff is
// the cascade ceiling, on a contributor — their own pay rate.
export const VistaStaffMeSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: VistaRoleSchema,
  showBalance: z.boolean(),
  rateCents: z.number().int().nullable(),
  // Every cabinet this person can switch into: their own role, whatever else
  // sits in user_roles, and 'contributor' when applicable (handleAuthMe
  // computes it fresh on every call — never cached on the JWT). Required,
  // not optional: the profile-menu switcher needs to tell "one role" from
  // "server forgot to compute it" the same way rateCents already does for null.
  roles: z.array(VistaMeRoleSchema),
});
export type VistaStaffMe = z.infer<typeof VistaStaffMeSchema>;

export const VistaContributorMeSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.literal('contributor'),
  rateCents: z.number().int().nullable(),
  // Interface language, decided per person and stored server-side rather than
  // sniffed from the browser: a contributor in Brazil on a phone someone lent
  // them should not have to fight the UI language on every login.
  uiLang: z.string(),
});
export type VistaContributorMe = z.infer<typeof VistaContributorMeSchema>;

export const VistaMeResSchema = z.union([VistaStaffMeSchema, VistaContributorMeSchema]);
export type VistaMeRes = z.infer<typeof VistaMeResSchema>;
